import { ESymbolCategory } from "@kuika/kuika-cl-model";
import type { CascaderOptionType } from "antd/es/cascader";
import React from "react";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import {
  ESymbolPickerActionOrder,
  type ISymbolPickerActionResultOrigin
} from "../../../../../shared/utility/symbol-picker-action-order";
import {
  ESymbolPickerTypeVerdict,
  type ISymbolPickerTypeCompatibility
} from "../../../../../shared/utility/symbol-picker-type-compat";
import type { ISymbolPickerCategoryDoc } from "../../../../../shared/utility/symbol-picker-value-docs";
import KuikaBadge, { type KuikaBadgeColor } from "../../../../ui-kit/kuika-badge/kuika-badge";
import KuikaButton from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInfoBox from "../../../../ui-kit/kuika-info-box/kuika-info-box";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../../../../ui-kit/kuika-tooltip/kuika-tooltip";
import { keepPickerFieldFocus } from "../picker-columns/use-picker-columns";
import {
  describeSymbolPickerCardinality,
  ESymbolPickerNodeKind,
  getSymbolPickerNodeChain,
  isSymbolPickerPickable,
  readSymbolPickerTypeName,
  type ISymbolPickerNode
} from "./symbol-picker-node";
import {
  findSymbolPickerActionResultId,
  findSymbolPickerComponentOption,
  getSymbolPickerNodeCategoryDoc,
  getSymbolPickerNodeCompatibility,
  getSymbolPickerNodeConversion,
  getSymbolPickerNodeDefaultDoc,
  type ISymbolPickerBindingTarget,
  type ISymbolPickerComponentFacts,
  type ISymbolPickerConversionPreview,
  type ISymbolPickerPreviewContext
} from "./symbol-picker-preview-model";

/**
 * What a category means, for the leaves whose name alone does not say what gets bound. Only the
 * categories whose binding is a fixed kind of thing; every other leaf is described by its own type
 * and path, read from the option.
 */
const CATEGORY_EXPLANATIONS: ReadonlyMap<ESymbolCategory, string> = new Map<ESymbolCategory, string>([
  [ESymbolCategory.fixed, "A constant. After picking, type the value into the field."],
  [ESymbolCategory.default, "A built-in value the app works out when it runs."],
  [ESymbolCategory.condition, "Picks one of two values depending on a condition you set next."]
]);

/** The value's type as the option states it: the drawn caption, else its type name and cardinality. */
export function getSymbolPickerTypeLabel(node: ISymbolPickerNode): string | undefined {
  if (node.typeCaption) {
    return node.typeCaption;
  }
  const option: ISymbolPickerNode["option"] = node.option;
  const typeName: string | undefined =
    readSymbolPickerTypeName(option.toType) ??
    readSymbolPickerTypeName(option.currentType) ??
    readSymbolPickerTypeName(option.domainType) ??
    readSymbolPickerTypeName(option.fieldType);
  if (!typeName) {
    return undefined;
  }
  const cardinality: string | undefined = describeSymbolPickerCardinality(option.cardinality);
  return cardinality === "List" ? `List of ${typeName}` : typeName;
}

export function getSymbolPickerExplanation(node: ISymbolPickerNode): string | undefined {
  const category: unknown = node.option.category;
  // A Sort Key is filed as a fixed value but binds the key it names, not something typed afterwards.
  if (typeof category !== "number" || node.option.isCustomSortKey === true) {
    return undefined;
  }
  return CATEGORY_EXPLANATIONS.get(category as ESymbolCategory);
}

interface ISectionProps {
  title: string;
  children: React.ReactNode;
}

/** A titled block — only for what runs over several lines (a conversion's explanation and example). */
const PreviewSection = ({ title, children }: ISectionProps): JSX.Element => (
  <section className="kuika-action-picker-popup__preview-section kuika-symbol-picker-popup__section">
    <h4 className="kuika-action-picker-popup__preview-heading">{title}</h4>
    {children}
  </section>
);

interface IFactProps {
  label: string;
  /** The full text on hover, for a value the one-line cell cuts short. Defaults to the value itself. */
  title?: string;
  testId?: string;
  children: React.ReactNode;
}

/**
 * One fact as a label / value pair of the facts grid: the label muted in the narrow left column, the
 * value on one line to its right, cut with an ellipsis and whole in its native tooltip.
 */
const Fact = ({ label, title, testId, children }: IFactProps): JSX.Element => (
  <>
    <dt className="kuika-symbol-picker-popup__fact-label">{label}</dt>
    <dd
      className="kuika-symbol-picker-popup__fact-value"
      title={title ?? (typeof children === "string" ? children : undefined)}
      data-testid={testId}
    >
      {children}
    </dd>
  </>
);

interface IBindingTargetProps {
  target: ISymbolPickerBindingTarget;
}

/** "Binding to" — the field the value is going into: one line of owner › name · type, then its doc line. */
const BindingTarget = ({ target }: IBindingTargetProps): JSX.Element => {
  const owner: string | undefined = target.componentName ?? target.actionName;
  const fullName: string = [owner ? `${owner} › ${target.name}` : target.name, target.typeLabel]
    .filter((part: string | undefined): part is string => Boolean(part))
    .join(" · ");
  return (
    <div className="kuika-symbol-picker-popup__target" data-testid="symbolPickerPreviewTarget">
      <div className="kuika-symbol-picker-popup__target-line">
        <span className="kuika-action-picker-popup__preview-heading">Binding to</span>
        <span className="kuika-symbol-picker-popup__target-name" title={fullName}>
          {owner && <span className="kuika-symbol-picker-popup__target-owner">{owner} › </span>}
          <span data-testid="symbolPickerPreviewTargetName">{target.name}</span>
          {target.typeLabel && (
            <>
              <span className="kuika-symbol-picker-popup__target-owner"> · </span>
              <span className="kuika-symbol-picker-popup__target-type" data-testid="symbolPickerPreviewTargetType">
                {target.typeLabel}
              </span>
            </>
          )}
        </span>
      </div>
      {target.description && (
        <p
          className="kuika-symbol-picker-popup__target-doc"
          title={target.description}
          data-testid="symbolPickerPreviewTargetDoc"
        >
          {target.description}
        </p>
      )}
    </div>
  );
};

const VERDICT_COLORS: ReadonlyMap<ESymbolPickerTypeVerdict, KuikaBadgeColor> = new Map<
  ESymbolPickerTypeVerdict,
  KuikaBadgeColor
>([
  [ESymbolPickerTypeVerdict.match, "green"],
  [ESymbolPickerTypeVerdict.convertible, "yellow"],
  [ESymbolPickerTypeVerdict.mismatch, "red"]
]);

interface ICompatibilityProps {
  compatibility: ISymbolPickerTypeCompatibility;
}

/**
 * The verdict on ONE badge line ("✓ Matches String"). A longer explanation, when the verdict has one,
 * is the badge's tooltip — never a second line repeating it.
 */
const CompatibilityBadge = ({ compatibility }: ICompatibilityProps): JSX.Element => {
  const badge: JSX.Element = (
    <span
      className="kuika-symbol-picker-popup__verdict"
      data-verdict={compatibility.verdict}
      title={compatibility.detail ? undefined : compatibility.label}
    >
      <KuikaBadge
        dataTestId="symbolPickerPreviewCompatibility"
        color={VERDICT_COLORS.get(compatibility.verdict)}
        content={compatibility.label}
      />
    </span>
  );
  if (!compatibility.detail) {
    return badge;
  }
  return (
    <KuikaTooltip
      content={compatibility.detail}
      position="left"
      contentTestId="symbolPickerPreviewCompatibilityMessage"
    >
      {badge}
    </KuikaTooltip>
  );
};

interface IDocsLinkProps {
  url: string;
}

const DocsLink = ({ url }: IDocsLinkProps): JSX.Element => (
  <a
    className="kuika-action-picker-popup__docs-link"
    href={url}
    target="_blank"
    rel="noopener noreferrer"
    data-testid="symbolPickerPreviewDocs"
    onMouseDown={keepPickerFieldFocus}
  >
    <span>Docs</span>
    <KuikaSvg name="external-link" type="common" width={12} height={12} />
  </a>
);

/** "getOrders (orders) — Button1 · onClick": the action, its tag, and where it runs. */
function describeActionOrigin(origin: ISymbolPickerActionResultOrigin): string {
  const where: string = [origin.ownerName, origin.eventName]
    .filter((part: string | undefined): part is string => Boolean(part))
    .join(" · ");
  const action: string = origin.actionTag ? `${origin.actionLabel} (${origin.actionTag})` : origin.actionLabel;
  return where ? `${action} — ${where}` : action;
}

interface IConversionProps {
  conversion: ISymbolPickerConversionPreview;
}

/** What a conversion does and an example of it — the multi-line part; its result type is a fact. */
const ConversionDoc = ({ conversion }: IConversionProps): JSX.Element | null => {
  if (!conversion.doc) {
    return null;
  }
  return (
    <PreviewSection title="Conversion">
      <p className="kuika-action-picker-popup__preview-muted">{conversion.doc.description}</p>
      {conversion.doc.example && (
        <code className="kuika-symbol-picker-popup__example" data-testid="symbolPickerPreviewConversionExample">
          {conversion.doc.example}
        </code>
      )}
    </PreviewSection>
  );
};

export interface ISymbolPickerPreviewProps {
  node: ISymbolPickerNode | null;
  onSelect: (node: ISymbolPickerNode) => void;
  context?: ISymbolPickerPreviewContext;
}

/**
 * One line about the pointed-at row: a Default value's own description, else what its kind of value
 * is (Fixed / Condition), else its category's line. A category ROW only ever gets the category line —
 * "Sort Keys" is filed under the Default category, but is not a built-in value.
 */
function getDescription(
  node: ISymbolPickerNode,
  categoryDoc: ISymbolPickerCategoryDoc | undefined
): string | undefined {
  if (node.kind === ESymbolPickerNodeKind.branch) {
    return categoryDoc?.description;
  }
  return getSymbolPickerNodeDefaultDoc(node) ?? getSymbolPickerExplanation(node) ?? categoryDoc?.description;
}

/**
 * The pane at the columns' open end: what the pointed-at row binds — the field it goes into, its
 * path, its type and whether that fits, where it comes from — read off the very option a pick hands
 * the host and off the host's own facts. Nothing is shown that neither carries.
 */
export const SymbolPickerPreview = ({ node, onSelect, context }: ISymbolPickerPreviewProps): JSX.Element => {
  const target: ISymbolPickerBindingTarget | undefined = context?.target;
  if (!node) {
    return (
      <div
        className="kuika-action-picker-popup__preview kuika-symbol-picker-popup__preview"
        data-testid="symbolPickerPreview"
      >
        {target && (
          <div className="kuika-symbol-picker-popup__preview-head">
            <BindingTarget target={target} />
          </div>
        )}
        <div className="kuika-action-picker-popup__preview--idle kuika-symbol-picker-popup__idle">
          <KuikaSvg name="info-circle" type="common" width={20} height={20} />
          <span>Point at a value to see what it binds.</span>
        </div>
      </div>
    );
  }

  const chain: ISymbolPickerNode[] = getSymbolPickerNodeChain(node);
  const trail: string = chain
    .slice(0, -1)
    .map((link: ISymbolPickerNode): string => link.name)
    .join(" › ");
  const source: string | undefined = chain.length > 1 ? chain[0].name : undefined;
  const typeLabel: string | undefined = getSymbolPickerTypeLabel(node);
  const categoryDoc: ISymbolPickerCategoryDoc | undefined = getSymbolPickerNodeCategoryDoc(node);
  const description: string | undefined = getDescription(node, categoryDoc);
  const isPickable: boolean = isSymbolPickerPickable(node);
  const compatibility: ISymbolPickerTypeCompatibility | undefined = getSymbolPickerNodeCompatibility(
    node,
    context?.targetShape
  );
  const conversion: ISymbolPickerConversionPreview | undefined = getSymbolPickerNodeConversion(
    node,
    context?.targetShape
  );
  const resultActionId: string | undefined = findSymbolPickerActionResultId(node);
  const origin: ISymbolPickerActionResultOrigin | undefined =
    resultActionId && context?.describeActionResult ? context.describeActionResult(resultActionId) : undefined;
  const componentOption: CascaderOptionType | undefined = findSymbolPickerComponentOption(node);
  const componentFacts: ISymbolPickerComponentFacts | undefined =
    componentOption && context?.describeComponent ? context.describeComponent(componentOption) : undefined;
  const docsUrl: string | undefined = KMainFunctions.isMainAI() ? undefined : categoryDoc?.docsUrl;
  const hasFacts: boolean = Boolean(
    typeLabel ||
    source ||
    conversion?.resultType ||
    componentFacts?.typeName ||
    componentFacts?.designValue ||
    componentFacts?.repeatingParentName ||
    origin
  );

  return (
    <div
      className="kuika-action-picker-popup__preview kuika-symbol-picker-popup__preview"
      data-testid="symbolPickerPreview"
    >
      <div className="kuika-action-picker-popup__preview-scroll">
        {target && <BindingTarget target={target} />}
        <div className="kuika-action-picker-popup__preview-title kuika-symbol-picker-popup__title">
          <span className="kuika-action-picker-popup__preview-name" data-testid="symbolPickerPreviewName">
            {node.name}
          </span>
          {trail && (
            <span className="kuika-action-picker-popup__preview-trail kuika-symbol-picker-popup__trail" title={trail}>
              {trail}
            </span>
          )}
        </div>
        {node.isDisabled && node.disabledReason && (
          <div className="kuika-action-picker-popup__banner" role="note">
            <KuikaSvg name="lock" type="common" width={14} height={14} />
            <span>{node.disabledReason}</span>
          </div>
        )}
        {compatibility && <CompatibilityBadge compatibility={compatibility} />}
        {description && (
          <p
            className="kuika-action-picker-popup__description kuika-symbol-picker-popup__description"
            title={description}
            data-testid="symbolPickerPreviewDescription"
          >
            {description}
          </p>
        )}
        {docsUrl && <DocsLink url={docsUrl} />}
        {hasFacts && (
          <dl className="kuika-symbol-picker-popup__facts">
            {typeLabel && (
              <Fact label="Type" testId="symbolPickerPreviewType">
                {typeLabel}
              </Fact>
            )}
            {source && (
              <Fact label="Source" testId="symbolPickerPreviewSource">
                {source}
              </Fact>
            )}
            {conversion?.resultType && (
              <Fact label="Result" testId="symbolPickerPreviewConversionResult">
                {conversion.resultType}
              </Fact>
            )}
            {componentFacts?.typeName && (
              <Fact label="Component" testId="symbolPickerPreviewComponentType">
                {componentFacts.typeName}
              </Fact>
            )}
            {componentFacts?.designValue && (
              <Fact label="In design" testId="symbolPickerPreviewDesignValue">
                {`“${componentFacts.designValue}”`}
              </Fact>
            )}
            {componentFacts?.repeatingParentName && (
              <Fact
                label="Per row"
                title={`Repeats inside ${componentFacts.repeatingParentName}, so each row reads its own value.`}
                testId="symbolPickerPreviewPerRow"
              >
                {`Repeats inside ${componentFacts.repeatingParentName}`}
              </Fact>
            )}
            {origin && (
              <Fact label="From" testId="symbolPickerPreviewOrigin">
                {describeActionOrigin(origin)}
              </Fact>
            )}
          </dl>
        )}
        {conversion && <ConversionDoc conversion={conversion} />}
        {origin?.order === ESymbolPickerActionOrder.after && (
          <KuikaInfoBox
            color="yellow"
            leftUnit={
              <span data-testid="symbolPickerPreviewOrderWarning">
                Runs after this step — value will be empty here.
              </span>
            }
          />
        )}
      </div>
      {isPickable && (
        <div className="kuika-action-picker-popup__preview-footer">
          <KuikaButton
            type="primary"
            size="sm"
            data-testid="symbolPickerPreviewSelect"
            onMouseDown={keepPickerFieldFocus}
            onClick={(): void => onSelect(node)}
          >
            Bind
          </KuikaButton>
          <span className="kuika-action-picker-popup__kbd">↵</span>
        </div>
      )}
    </div>
  );
};
